import React, { useState, useId } from 'react';
import styles from './InputField.module.css';

type InputFieldProps = {
    label?: string;
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'date' | 'url';
    disabled?: boolean;
    required?: boolean;
    feedback?: 'success' | 'error' | 'warning';
    feedbackMessage?: string;
    name?: string;
    id?: string;
    autoComplete?: string;
    maxLength?: number;
    unit?: string;
    inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
    pattern?: string;
    min?: number | string;
    max?: number | string;
    dir?: 'ltr' | 'rtl' | 'auto';
};

const InputField: React.FC<InputFieldProps> = ({
    label = '',
    value = '',
    onChange = () => {},
    placeholder = '',
    type = 'text',
    disabled = false,
    required = false,
    feedback,
    feedbackMessage,
    name,
    id,
    autoComplete,
    maxLength,
    unit,
    inputMode,
    pattern,
    min,
    max,
    dir,
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const generatedId = useId();
    const inputId = id || `input-${generatedId}`;
    const feedbackId = feedbackMessage ? `${inputId}-feedback` : undefined;
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value);
    return (
        <div className={styles.inputFieldContainer}>
            <div className={styles.inputWrapper}>
                <input
                    id={inputId}
                    type={type === 'password' && showPassword ? 'text' : type}
                    className={`${styles.input} ${disabled ? styles.disabled : ''} ${feedback ? styles[feedback] : ''}`}
                    value={value}
                    onChange={handleChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    required={required}
                    name={name}
                    autoComplete={autoComplete}
                    maxLength={maxLength}
                    inputMode={inputMode}
                    pattern={pattern}
                    min={min}
                    max={max}
                    aria-invalid={feedback === 'error' ? true : undefined}
                    aria-describedby={feedbackId}
                    dir={dir}
                />
                <label htmlFor={inputId} className={styles.label}>{label} {required && <span className={styles.required}>*</span>}</label>
                {unit && <span className={styles.unit}>{unit}</span>}
                <div className={styles.inputControllor}>
                    {type === 'password' && (
                        <button type="button" className={styles.passwordToggle} onClick={() => setShowPassword(!showPassword)} disabled={disabled} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                            {showPassword ? (
                                <svg width="25" height="25" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 12C4 12 5.6 7 12 7M12 7C18.4 7 20 12 20 12M12 7V4M18 5L16 7.5M6 5L8 7.5M15 13C15 14.6569 13.6569 16 12 16C10.3431 16 9 14.6569 9 13C9 11.3431 10.3431 10 12 10C13.6569 10 15 11.3431 15 13Z" />
                                </svg>
                            ) : (
                                <svg width="25" height="25" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 10C4 10 5.6 15 12 15M12 15C18.4 15 20 10 20 10M12 15V18M18 17L16 14.5M6 17L8 14.5" />
                                </svg>
                            )}
                        </button>
                    )}
                    {feedback && <span className={`${styles.feedbackIcon} ${styles[feedback]}`}>{feedback === 'error' && '!'}</span>}
                </div>
                <div className={styles.border}></div>
            </div>
            {feedbackMessage && <div id={feedbackId} className={`${styles.feedbackMessage} ${feedback ? styles[feedback] : ''}`} role="alert">{feedbackMessage}</div>}
        </div>
    );
};

export default InputField;
